import { Client } from '@microsoft/microsoft-graph-client'
import { accessRequest } from '../utils/authConfig'
import { isTokenValid, retrieveTokenData, storeTokenData } from '../utils/storageUtils'
import { fetchAccessTokenForCognitoUser } from '../utils/authUtils'

class MSGraphService {
  constructor() {
    this.cache = {
      userData: null,
      timestamp: null,
      accessToken: null
    }

    this.cacheExpirationTime = 5 * 60 * 1000
    this.pendingRequests = {}
  }

  /**
   * Checks if a value is a valid image Blob
   * @param {any} value - The value to check
   * @returns {boolean} True if the value is an image Blob
   */
  isValidImageBlob(value) {
    return value instanceof Blob &&
      value.type &&
      value.type.startsWith('image/') &&
      value.size > 0
  }

  /**
   * Checks if the cache is valid for the given token
   * @param {string} accessToken - The access token to validate against
   * @returns {boolean} Whether the cache is valid
   */
  isCacheValid(accessToken) {
    const currentTime = Date.now()
    return (
      this.cache.userData &&
      this.cache.timestamp &&
      currentTime - this.cache.timestamp < this.cacheExpirationTime &&
      this.cache.accessToken === accessToken
    )
  }

  /**
   * Clears the service cache
   */
  clearCache() {
    // Revoke any blob URLs to prevent memory leaks
    if (this.cache.userData && this.cache.userData.photo) {
      try {
        URL.revokeObjectURL(this.cache.userData.photo)
      } catch (error) {
        console.warn('Error revoking object URL:', error)
      }
    }

    this.cache = {
      userData: null,
      timestamp: null,
      accessToken: null
    }
    this.pendingRequests = {}
  }

  /**
   * Gets user data either from cache or from API
   * @param {string} accessToken - The authentication token
   * @returns {Promise<Object>} User data including profile info and photo
   */
  async getUserData(accessToken) {

    // Create a unique request identifier
    const requestId = `userData-${accessToken}`

    // Return empty data if no token
    if (!accessToken) {
      return {
        displayName: '',
        mail: '',
        photo: null
      }
    }

    if (this.isCacheValid(accessToken)) {
      return this.cache.userData
    }

    if (this.pendingRequests[requestId]) {
      return this.pendingRequests[requestId]
    }

    this.pendingRequests[requestId] = this.fetchUserData(accessToken)

    try {
      const result = await this.pendingRequests[requestId]
      return result
    } finally {
      delete this.pendingRequests[requestId]
    }
  }

  /**
   * Fetches fresh user data from Microsoft Graph API
   * @param {string} accessToken - The authentication token
   * @returns {Promise<Object>} User data
   */
  async fetchUserData(accessToken) {
    // Initialize default return object
    const userData = {
      displayName: '',
      mail: '',
      photo: null
    }

    // Initialize Graph client
    const client = Client.init({
      authProvider: (done) => done(null, accessToken)
    })

    try {
      // Get basic user info
      const user = await client.api('/me').get()
      userData.displayName = user.displayName || ''
      userData.mail = user.mail || user.userPrincipalName || ''

      // If we have user info, try to get the photo
      if (userData.displayName) {
        try {
          const profilePicture = await client
            .api('/me/photo/$value')
            .get()

          if (this.isValidImageBlob(profilePicture)) {
            userData.photo = URL.createObjectURL(profilePicture)
          }
        } catch {

        }
      }

      // Cache the results
      this.cache.userData = userData
      this.cache.timestamp = Date.now()
      this.cache.accessToken = accessToken

      return userData
    } catch {
      return userData
    }
  }
}

// Create a single instance of the service
const graphService = new MSGraphService()

class UserAccessService {
  constructor() {
    this.pendingRequest = null
    this.lastRequestTime = null
    this.requestCooldown = 1000
  }

  /**
   * Clears the service state
   */
  clearService() {
    this.pendingRequest = null
    this.lastRequestTime = null
  }

  /**
   * Calls the Kolumbus User Access API with deduplication
   * @param {Object} msalInstance - MSAL instance
   * @returns {Promise<Object>} User access data
   */
  async callUserAccessAPI(msalInstance) {
    // If there's already a pending request, return it
    if (this.pendingRequest) {
      return this.pendingRequest
    }

    const now = Date.now()
    if (this.lastRequestTime && (now - this.lastRequestTime) < this.requestCooldown) {
      throw new Error('Request made too soon after previous request')
    }

    // Create and store the pending request
    this.pendingRequest = this._executeUserAccessCall(msalInstance)
    this.lastRequestTime = now

    try {
      const result = await this.pendingRequest
      return result
    } finally {
      // Clear the pending request after completion
      this.pendingRequest = null
    }
  }

  /**
   * Internal method to execute the actual API call
   * @param {Object} msalInstance - MSAL instance
   * @returns {Promise<Object>} User access data
   */
  async _executeUserAccessCall(msalInstance) {
    try {
      // Get valid access token using the same logic as axios interceptor
      const accessToken = await getValidAccessToken(msalInstance)

      if (!accessToken) {
        throw new Error('Access token is required')
      }

      //Kolumbus API endpoint
            const endpoint = `${process.env.REACT_APP_API_ENDPOINT}/generic/fetchUserAccess`
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const userAccessData = await response.json()

      // Store the user access data in localStorage for later use
      if (userAccessData) {
        localStorage.setItem('userAccessData', JSON.stringify(userAccessData))
        localStorage.setItem('userAccessDataTimestamp', Date.now().toString())
      }

      return userAccessData

    } catch (error) {
      console.error('Error in user access API call:', error)
      throw new Error(`Failed to fetch user access data: ${error.message}`)
    }
  }
}

// Create a single instance of the user access service
const userAccessService = new UserAccessService()

/**
 * Gets valid access token based on login type
 * @param {Object} msalInstance - MSAL instance (optional, for MSAL authentication)
 * @returns {Promise<string>} Valid access token
 */
export const getValidAccessToken = async (msalInstance) => {
  try {
    const loginType = localStorage.getItem('loginType')
    let validToken = ''

    if (loginType === 'cognito') {
      // Handle Cognito authentication
      validToken = await fetchAccessTokenForCognitoUser()
    } else if (msalInstance) {
      // Handle MSAL authentication
      const tokenData = await retrieveTokenData(msalInstance)

      if (isTokenValid(tokenData)) {
        validToken = tokenData.accessToken
      } else {
        // Token is not valid, acquire a new one
        const getNewToken = await msalInstance.acquireTokenSilent({
          ...accessRequest
        })

        const tokenObject = {
          accessToken: getNewToken?.accessToken || '',
          expiresOn: getNewToken?.expiresOn || '',
          extExpiresOn: getNewToken?.extExpiresOn || ''
        }

        storeTokenData(tokenObject)
        validToken = tokenObject.accessToken
      }
    } else {
      throw new Error('No user logged in')
    }

    return validToken
  } catch (error) {
    console.error('Error getting valid access token:', error)
    throw new Error(`Failed to get access token: ${error.message}`)
  }
}

/**
 * Gets user data from Microsoft Graph (cached when possible)
 * @param {string} accessToken - The authentication token
 * @returns {Promise<Object>} User data including profile info and photo
 */
export async function getUserData(accessToken) {
  return graphService.getUserData(accessToken)
}

/**
 * Gets basic user profile information
 * @param {string} accessToken - The authentication token
 * @returns {Promise<Object>} Basic user profile data
 */
export async function callMsGraph(accessToken) {
  if (!accessToken) {
    return {}
  }

  try {
    const userData = await getUserData(accessToken)
    // Return only the basic profile data without the photo
    return {
      displayName: userData.displayName,
      mail: userData.mail
    }
  } catch {
    return {}
  }
}

/**
 * Gets user data including photo
 * @param {string} accessToken - The authentication token
 * @returns {Promise<Object>} User data including photo
 */
export async function callMsGraphForPhoto(accessToken) {
  return await getUserData(accessToken)
}

/**
 * Calls the Kolumbus User Access API to fetch user access data (with deduplication)
 * @param {Object} msalInstance - MSAL instance (optional, for getting access token)
 * @returns {Promise<Object>} User access data
 */
export const callKolumbusUserAccess = async (msalInstance) => {
  return userAccessService.callUserAccessAPI(msalInstance)
}

/**
 * Gets cached user access data from localStorage
 * @returns {Object|null} Cached user access data or null if not found/expired
 */
export const getCachedUserAccess = () => {
  try {
    const cachedData = localStorage.getItem('userAccessData')
    const timestamp = localStorage.getItem('userAccessDataTimestamp')

    if (!cachedData || !timestamp) {
      return null
    }

    // Check if cache is expired (e.g., 1 hour = 3600000 ms)
    const CACHE_DURATION = 60 * 60 * 1000 // 1 hour
    const isExpired = Date.now() - parseInt(timestamp) > CACHE_DURATION

    if (isExpired) {
      localStorage.removeItem('userAccessData')
      localStorage.removeItem('userAccessDataTimestamp')
      return null
    }

    return JSON.parse(cachedData)
  } catch (error) {
    console.error('Error getting cached user access data:', error)
    return null
  }
}

/**
 * Clears user access data from localStorage
 */
export const clearUserAccessCache = () => {
  try {
    localStorage.removeItem('userAccessData')
    localStorage.removeItem('userAccessDataTimestamp')
  } catch (error) {
    console.error('Error clearing user access cache:', error)
  }
}

/**
 * Comprehensive cleanup function - clears all user data and caches
 */
export const clearAllUserData = () => {
  try {
    // Clear user access cache
    clearUserAccessCache()

    // Clear MSGraph service cache
    if (graphService) {
      graphService.clearCache()
    }

    // Clear user access service state
    if (userAccessService) {
      userAccessService.clearService()
    }

    // Clear all application-specific localStorage items
    const keysToRemove = [
      'authenticated',
      'loginType',
      'userAccessData',
      'userAccessDataTimestamp',
      'tokenData',
      'userStores',
      'selectedStore',
      'userRole',
      'cognitoTokens',
      'lastLoginTime',
      'sessionData',
      'ms.cache',
      'msal',
      'adal.cache',
      'amplify-signin-with-hostedUI',
      'amplify-redirected-from-hosted-ui',
      'CognitoIdentityServiceProvider'
    ]

    // Remove specific keys first
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key)
      } catch (error) {
        console.warn(`Error removing localStorage key ${key}:`, error)
      }
    })

    // Clear any keys that start with common prefixes
    const prefixesToClear = [
      'msal.',
      'adal.',
      'amplify-',
      'CognitoIdentityServiceProvider.',
      'aws-amplify-',
      'kolumbus-'
    ]

    // Get all localStorage keys and remove matching ones
    const allKeys = Object.keys(localStorage)
    allKeys.forEach(key => {
      prefixesToClear.forEach(prefix => {
        if (key.startsWith(prefix)) {
          try {
            localStorage.removeItem(key)
          } catch (error) {
            console.warn(`Error removing prefixed key ${key}:`, error)
          }
        }
      })
    })

    // Clear sessionStorage completely
    try {
      sessionStorage.clear()
    } catch (error) {
      console.warn('Error clearing sessionStorage:', error)
    }

    // Clear any IndexedDB data if present (for MSAL)
    if (window.indexedDB) {
      try {
        // This is asynchronous but we don't wait for it
        const deleteReq = indexedDB.deleteDatabase('msal-cache')
        deleteReq.onsuccess = () => { }
        deleteReq.onerror = () => { }
      } catch (error) {
        console.warn('Error clearing IndexedDB:', error)
      }
    }
  } catch (error) {
    console.error('Error during comprehensive cleanup:', error)
  }
}

/**
 * Emergency cleanup - use as last resort
 */
export const emergencyCleanup = () => {
  try {
    // Clear everything
    localStorage.clear()
    sessionStorage.clear()

    // Clear cookies (best effort)
    document.cookie.split(';').forEach(function (c) {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
    })
  } catch (error) {
    console.error('Emergency cleanup failed:', error)
  }
}